import { useForm, useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import type { StationSetting } from '@transreport/pauk-rrms-api'
import {
  getGetStationSettingQueryKey,
  useGetStationSetting,
  useUpdateStationSetting,
} from '@transreport/pauk-rrms-api'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AppButton,
  AppSwitchToggle,
  SkeletonBlock,
  SkeletonButtonRow,
  SkeletonField,
  UnsavedChangesBlocker,
} from '@/components/framework'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { useStationStore } from '@/lib/stores/station'

type StationManagerFormState = {
  defaultDurationMinutes: string
  autoUpdateAssistanceJobTime: boolean
  minimalJourneyTransitionTime: string
  unassignedAssistanceDueAlarmEnabled: boolean
  unassignedAssistanceDueAlarmMinutes: string
}

const DEFAULT_MINIMAL_JOURNEY_TRANSITION_TIME_SECONDS = '0'
const DEFAULT_UNASSIGNED_ALARM_MINUTES = '10'
const MAX_DURATION_MINUTES = 360
const MAX_ALARM_LEAD_TIME_MINUTES = 60
const MAX_TRANSITION_TIME_SECONDS = 3_600
const numberInputGroupClass =
  'flex rounded border border-border-gray bg-white focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-primary'

const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
  event.currentTarget.blur()
}

const DEFAULT_FORM_STATE: StationManagerFormState = {
  defaultDurationMinutes: '',
  autoUpdateAssistanceJobTime: false,
  minimalJourneyTransitionTime: DEFAULT_MINIMAL_JOURNEY_TRANSITION_TIME_SECONDS,
  unassignedAssistanceDueAlarmEnabled: false,
  unassignedAssistanceDueAlarmMinutes: DEFAULT_UNASSIGNED_ALARM_MINUTES,
}

const mapStationSettingToFormState = (stationSetting: StationSetting): StationManagerFormState => ({
  defaultDurationMinutes: Math.round(
    stationSetting.default_assistance_job_duration / 60
  ).toString(),
  autoUpdateAssistanceJobTime: stationSetting.auto_update_assistance_job_time ?? false,
  minimalJourneyTransitionTime: (
    stationSetting.minimal_journey_transition_time ??
    Number(DEFAULT_MINIMAL_JOURNEY_TRANSITION_TIME_SECONDS)
  ).toString(),
  unassignedAssistanceDueAlarmEnabled:
    stationSetting.unassigned_assistance_due_alarm_enabled ?? false,
  unassignedAssistanceDueAlarmMinutes: Math.round(
    (stationSetting.unassigned_assistance_due_alarm_time ??
      Number(DEFAULT_UNASSIGNED_ALARM_MINUTES) * 60) / 60
  ).toString(),
})

const parseWholeNumber = (value: string): number | null => {
  const normalizedValue = value.trim()

  if (!/^\d+$/.test(normalizedValue)) {
    return null
  }

  const parsedValue = Number(normalizedValue)
  return Number.isSafeInteger(parsedValue) ? parsedValue : null
}

const StationManagerFormSkeleton = () => (
  <section
    className="mx-auto w-full max-w-5xl rounded-lg border border-gray-100 bg-white p-8 shadow-sm"
    aria-label="station-manager-skeleton"
  >
    <div className="space-y-6" aria-hidden="true">
      <div className="space-y-2">
        <SkeletonBlock className="h-5 w-64" />
        <SkeletonBlock className="h-4 w-80" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <SkeletonField />
        <SkeletonField />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <SkeletonBlock className="h-24 w-full" />
        <SkeletonBlock className="h-24 w-full" />
      </div>

      <SkeletonButtonRow buttons={1} />
    </div>
  </section>
)

export const StationManagerPage: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [isFormInitialized, setIsFormInitialized] = useState(false)
  const stationForm = useForm({ defaultValues: DEFAULT_FORM_STATE })
  const formValues = useStore(stationForm.store, (s) => s.values)
  const hasChanges = useStore(stationForm.store, (s) => s.isDirty)

  const { stations, selectedStationCode } = useOperatingStations()
  const setStationSelectionLocked = useStationStore((state) => state.setStationSelectionLocked)
  const stationSettingQuery = useGetStationSetting(selectedStationCode, {
    query: { enabled: Boolean(selectedStationCode) },
  })

  const updateStationSettingMutation = useUpdateStationSetting({
    mutation: {
      meta: {
        toast: {
          success: t('stationManager.toast.updateSuccess'),
          error: t('stationManager.toast.saveError'),
        },
      },
    },
  })

  const isSaving = updateStationSettingMutation.isPending

  useEffect(() => {
    setStationSelectionLocked(hasChanges || isSaving)
    return () => setStationSelectionLocked(false)
  }, [hasChanges, isSaving, setStationSelectionLocked])

  const selectedStation = stations.find((station) => station.code === selectedStationCode)

  useEffect(() => {
    stationForm.reset(DEFAULT_FORM_STATE, { keepDefaultValues: true })
    setIsFormInitialized(false)
  }, [selectedStationCode, stationForm])

  useEffect(() => {
    if (!selectedStation || stationSettingQuery.isPending || !stationSettingQuery.data) {
      return
    }

    stationForm.reset(mapStationSettingToFormState(stationSettingQuery.data), {
      keepDefaultValues: true,
    })
    setIsFormInitialized(true)
  }, [stationForm, selectedStation, stationSettingQuery.data, stationSettingQuery.isPending])

  const isInitialLoading = stationSettingQuery.isPending && !stationSettingQuery.data

  const hasBlockingLoadError =
    Boolean(selectedStationCode) && Boolean(stationSettingQuery.error) && !stationSettingQuery.data

  const durationMinutes = parseWholeNumber(formValues.defaultDurationMinutes)
  const transitionTimeSeconds = parseWholeNumber(formValues.minimalJourneyTransitionTime)
  const alarmTimeMinutes = parseWholeNumber(formValues.unassignedAssistanceDueAlarmMinutes)

  const isDurationValid =
    durationMinutes !== null && durationMinutes > 0 && durationMinutes <= MAX_DURATION_MINUTES
  const isTransitionTimeValid =
    transitionTimeSeconds !== null && transitionTimeSeconds <= MAX_TRANSITION_TIME_SECONDS
  const isAlarmTimeValid =
    alarmTimeMinutes !== null &&
    alarmTimeMinutes > 0 &&
    alarmTimeMinutes <= MAX_ALARM_LEAD_TIME_MINUTES

  const isFormValid = isDurationValid && isTransitionTimeValid && isAlarmTimeValid

  const isSaveDisabled = !selectedStation || !isFormValid || !hasChanges || isSaving

  const handleSave = async () => {
    if (
      !selectedStation ||
      !isFormValid ||
      isSaving ||
      durationMinutes === null ||
      transitionTimeSeconds === null ||
      alarmTimeMinutes === null
    ) {
      return
    }

    const payload = {
      default_assistance_job_duration: durationMinutes * 60,
      auto_update_assistance_job_time: formValues.autoUpdateAssistanceJobTime,
      minimal_journey_transition_time: transitionTimeSeconds,
      unassigned_assistance_due_alarm_enabled: formValues.unassignedAssistanceDueAlarmEnabled,
      unassigned_assistance_due_alarm_time: alarmTimeMinutes * 60,
    }

    try {
      const savedSetting: StationSetting = await updateStationSettingMutation.mutateAsync({
        stationCode: selectedStationCode,
        data: payload,
      })
      const stationSettingQueryKey = getGetStationSettingQueryKey(selectedStationCode)
      queryClient.setQueryData(stationSettingQueryKey, savedSetting)
      void queryClient.invalidateQueries({ queryKey: stationSettingQueryKey })

      stationForm.reset(mapStationSettingToFormState(savedSetting), { keepDefaultValues: true })
    } catch {
      // The global mutation handler displays the error.
    }
  }

  return (
    <div className="flex size-full flex-col bg-gray-50">
      <UnsavedChangesBlocker isDirty={hasChanges} />
      <h1 className="sr-only">{t('stationManager.title')}</h1>

      <div className="flex-1 overflow-auto px-3 py-4 sm:p-6">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
          {isInitialLoading && <StationManagerFormSkeleton />}

          {!isInitialLoading && hasBlockingLoadError && (
            <section
              role="alert"
              className="mx-auto w-full max-w-5xl rounded-lg border border-red-300 bg-red-50 p-6 shadow-sm"
            >
              <h2 className="text-base font-semibold text-red-700">
                {t('stationManager.loadErrorTitle')}
              </h2>
              <p className="mt-2 text-sm text-red-700">
                {t('stationManager.loadErrorDescription')}
              </p>
              <div className="mt-4">
                <AppButton
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void stationSettingQuery.refetch()
                  }}
                >
                  {t('stationManager.retry')}
                </AppButton>
              </div>
            </section>
          )}

          {!isInitialLoading && !hasBlockingLoadError && selectedStation && isFormInitialized && (
            <section className="mx-auto w-full max-w-5xl rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-8">
              <form
                className="space-y-6"
                onSubmit={(event) => {
                  event.preventDefault()
                  void handleSave()
                }}
              >
                <div className="space-y-4">
                  <div className="grid gap-3 rounded border border-gray-200 bg-gray-50 px-4 py-3 md:grid-cols-[minmax(0,1fr)_16rem] md:items-center">
                    <div>
                      <label
                        htmlFor="station-manager-default-duration"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t('stationManager.defaultDurationLabel')}
                        <span className="sr-only"> ({t('stationManager.minutes')})</span>
                      </label>
                      <p
                        id="station-manager-default-duration-help"
                        className="text-xs text-gray-600"
                      >
                        {t('stationManager.defaultDurationHelp')}
                      </p>
                    </div>
                    <div>
                      <div className={numberInputGroupClass}>
                        <input
                          id="station-manager-default-duration"
                          type="number"
                          min={1}
                          max={MAX_DURATION_MINUTES}
                          step={1}
                          inputMode="numeric"
                          value={formValues.defaultDurationMinutes}
                          aria-invalid={!isDurationValid}
                          aria-describedby={`station-manager-default-duration-help${
                            !isDurationValid ? ' station-manager-duration-error' : ''
                          }`}
                          onChange={(event) =>
                            stationForm.setFieldValue('defaultDurationMinutes', event.target.value)
                          }
                          onWheel={preventWheelValueChange}
                          className="station-setting-number-input min-w-0 flex-1 rounded-l px-3 py-2 text-sm"
                        />
                        <span
                          className="flex items-center rounded-r border-l border-border-gray bg-gray-100 px-3 text-sm text-gray-600"
                          aria-hidden="true"
                        >
                          {t('stationManager.minutes')}
                        </span>
                      </div>
                      {!isDurationValid && (
                        <p id="station-manager-duration-error" className="mt-1 text-xs text-danger">
                          {t('stationManager.validation.duration')}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-3 rounded border border-gray-200 bg-gray-50 px-4 py-3 md:grid-cols-[minmax(0,1fr)_16rem] md:items-center">
                    <div>
                      <label
                        htmlFor="station-manager-transition-time"
                        className="block text-sm font-medium text-gray-700"
                      >
                        {t('stationManager.transitionTimeLabel')}
                        <span className="sr-only"> ({t('stationManager.seconds')})</span>
                      </label>
                      <p
                        id="station-manager-transition-time-help"
                        className="text-xs text-gray-600"
                      >
                        {t('stationManager.transitionTimeHelp')}
                      </p>
                    </div>
                    <div>
                      <div className={numberInputGroupClass}>
                        <input
                          id="station-manager-transition-time"
                          type="number"
                          min={0}
                          max={MAX_TRANSITION_TIME_SECONDS}
                          step={1}
                          inputMode="numeric"
                          value={formValues.minimalJourneyTransitionTime}
                          aria-invalid={!isTransitionTimeValid}
                          aria-describedby={`station-manager-transition-time-help${
                            !isTransitionTimeValid ? ' station-manager-transition-error' : ''
                          }`}
                          onChange={(event) =>
                            stationForm.setFieldValue(
                              'minimalJourneyTransitionTime',
                              event.target.value
                            )
                          }
                          onWheel={preventWheelValueChange}
                          className="station-setting-number-input min-w-0 flex-1 rounded-l px-3 py-2 text-sm"
                        />
                        <span
                          className="flex items-center rounded-r border-l border-border-gray bg-gray-100 px-3 text-sm text-gray-600"
                          aria-hidden="true"
                        >
                          {t('stationManager.seconds')}
                        </span>
                      </div>
                      {!isTransitionTimeValid && (
                        <p
                          id="station-manager-transition-error"
                          className="mt-1 text-xs text-danger"
                        >
                          {t('stationManager.validation.transition')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid gap-3 rounded border border-gray-200 bg-gray-50 px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                    <div>
                      <p
                        id="station-manager-auto-update-label"
                        className="text-sm font-medium text-gray-900"
                      >
                        {t('stationManager.autoUpdateLabel')}
                      </p>
                      <p id="station-manager-auto-update-help" className="text-xs text-gray-600">
                        {t('stationManager.autoUpdateHelp')}
                      </p>
                    </div>
                    <div className="md:justify-self-end">
                      <AppSwitchToggle
                        value={formValues.autoUpdateAssistanceJobTime}
                        ariaLabelledBy="station-manager-auto-update-label"
                        ariaDescribedBy="station-manager-auto-update-help"
                        onChange={(value) =>
                          stationForm.setFieldValue('autoUpdateAssistanceJobTime', value)
                        }
                      />
                    </div>
                  </div>

                  <section className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h2 className="text-sm font-semibold text-red-800">
                      {t('stationManager.alertSectionTitle')}
                    </h2>
                    <p className="mt-1 text-xs text-red-700">
                      {t('stationManager.alertSectionDescription')}
                    </p>

                    <div className="mt-4 grid gap-3 rounded border border-red-200 bg-white px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                      <div>
                        <p
                          id="station-manager-alarm-enabled-label"
                          className="text-sm font-medium text-gray-900"
                        >
                          {t('stationManager.alarmEnabledLabel')}
                        </p>
                        <p
                          id="station-manager-alarm-enabled-help"
                          className="text-xs text-gray-600"
                        >
                          {t('stationManager.alarmEnabledHelp')}
                        </p>
                      </div>
                      <div className="md:justify-self-end">
                        <AppSwitchToggle
                          value={formValues.unassignedAssistanceDueAlarmEnabled}
                          ariaLabelledBy="station-manager-alarm-enabled-label"
                          ariaDescribedBy="station-manager-alarm-enabled-help"
                          onChange={(value) =>
                            stationForm.setFieldValue('unassignedAssistanceDueAlarmEnabled', value)
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 rounded border border-red-200 bg-white px-4 py-3 md:grid-cols-[minmax(0,1fr)_16rem] md:items-center">
                      <div>
                        <label
                          htmlFor="station-manager-alarm-time"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('stationManager.alarmTimeLabel')}
                          <span className="sr-only"> ({t('stationManager.minutes')})</span>
                        </label>
                        <p id="station-manager-alarm-time-help" className="text-xs text-gray-600">
                          {t('stationManager.alarmTimeHelp')}
                        </p>
                      </div>
                      <div>
                        <div className={numberInputGroupClass}>
                          <input
                            id="station-manager-alarm-time"
                            type="number"
                            min={1}
                            max={MAX_ALARM_LEAD_TIME_MINUTES}
                            step={1}
                            inputMode="numeric"
                            value={formValues.unassignedAssistanceDueAlarmMinutes}
                            aria-invalid={!isAlarmTimeValid}
                            aria-describedby={`station-manager-alarm-time-help${
                              !isAlarmTimeValid ? ' station-manager-alarm-time-error' : ''
                            }`}
                            disabled={!formValues.unassignedAssistanceDueAlarmEnabled}
                            onChange={(event) =>
                              stationForm.setFieldValue(
                                'unassignedAssistanceDueAlarmMinutes',
                                event.target.value
                              )
                            }
                            onWheel={preventWheelValueChange}
                            className="station-setting-number-input min-w-0 flex-1 rounded-l px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                          />
                          <span
                            className="flex items-center rounded-r border-l border-border-gray bg-gray-100 px-3 text-sm text-gray-600"
                            aria-hidden="true"
                          >
                            {t('stationManager.minutes')}
                          </span>
                        </div>
                        {!isAlarmTimeValid && (
                          <p
                            id="station-manager-alarm-time-error"
                            className="mt-1 text-xs text-danger"
                          >
                            {t('stationManager.validation.alarm')}
                          </p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>

                <div className="flex justify-end border-t border-gray-100 pt-4">
                  <AppButton
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={isSaving}
                    disabled={isSaveDisabled}
                  >
                    {t('stationManager.save')}
                  </AppButton>
                </div>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}
